import type { DomainGuide } from '@/components/guide/guide-types';

import th from './th.json';
import en from './en.json';
import zh from './zh.json';
import vi from './vi.json';
import km from './km.json';
import lo from './lo.json';
import my from './my.json';

/**
 * Help catalog for the `audit` domain — the two tabs of `/audit`.
 *
 * The trail is real but partial, and a reader who assumes it is complete will
 * draw a wrong conclusion from an absent row:
 *
 *   - Logins, failed logins and logouts never reach it; svc-auth keeps them in
 *     `auth.login_attempts` and `auth.sessions` and emits an audit event only
 *     for impersonation.
 *   - Nothing in svc-setting emits at all, so the whole Settings page is
 *     invisible here.
 *   - `actorUsername` arrives ON the event from exactly two call sites, both in
 *     apps/admin/src/routes/auth-sessions.ts. Every other row is named at INSERT
 *     time instead, by the consumer resolving `actorId` against svc-admin /
 *     svc-member — both the mapped path and the `audit.*` fast path. So an
 *     `admin` or `member` actor has a name; `system` and `service` rows name
 *     nobody by design, and a lookup that cannot answer leaves the column NULL
 *     rather than baking in the raw id, which keeps the row resolvable on a
 *     later read (the table refuses UPDATE).
 *   - Staff actions are emitted with no `agentId` (apps/admin/src/repo/
 *     admins.ts:260,456,519,557), and the scoped read filters on that column —
 *     so a brand-scoped operator sees none of them.
 *   - Emission is best-effort and swallows its own failures
 *     (packages/shared/src/clients/audit-emitter.ts:85-97).
 *
 * Hence `partially-wired` rather than `live`. What IS there is immutable: a
 * BEFORE trigger refuses UPDATE outright and DELETE unless the transaction
 * opts in (apps/audit/db/migrations/0003_audit_append_only.sql).
 *
 * The security-events tab has no producer anywhere in the repo — nothing calls
 * `POST /security-events/internal` — so it is empty in every environment and
 * the event-type dropdown is a schema, not a feed. `records-intent`.
 */
export const auditGuide: DomainGuide = {
  domain: 'audit',
  pages: [
    {
      id: 'audit.logs',
      route: '/audit → Logs',
      // Gateway: audit-logs reads = audit:read (upstreams.ts:405).
      permission: 'audit:read',
      status: 'partially-wired',
      fields: [
        { key: 'createdAt', kind: 'date' },
        { key: 'actorKind', kind: 'text' },
        { key: 'actor', kind: 'text' },
        { key: 'action', kind: 'text' },
        { key: 'resource', kind: 'text' },
        { key: 'resourceId', kind: 'text' },
        { key: 'ipAddress', kind: 'text' },
        { key: 'device', kind: 'text' },
        { key: 'gps', kind: 'text' },
      ],
      cautionCount: 7,
      warningCount: 0,
    },
    {
      // The one action the reveal endpoint writes (ADR-0003). Same component as
      // the Logs tab above, pinned to it, so the two cannot describe the same
      // columns differently.
      id: 'audit.sensitiveViews',
      route: '/audit → Sensitive views',
      // Gateway: audit-logs reads = audit:read (upstreams.ts). Seeing who looked
      // is a supervisor's job and rides the same code as the trail itself.
      permission: 'audit:read',
      // The feed has a real producer, unlike the security-events tab below — but
      // `emitAudit` swallows its own failures, so an absent row is not proof
      // that nobody looked.
      status: 'partially-wired',
      fields: [
        { key: 'createdAt', kind: 'date' },
        { key: 'actorKind', kind: 'text' },
        { key: 'actor', kind: 'text' },
        { key: 'action', kind: 'text' },
        { key: 'resource', kind: 'text' },
        { key: 'resourceId', kind: 'text' },
        { key: 'revealedField', kind: 'text' },
        { key: 'ipAddress', kind: 'text' },
        { key: 'device', kind: 'text' },
        { key: 'gps', kind: 'text' },
      ],
      cautionCount: 4,
      warningCount: 0,
    },
    {
      /**
       * The review trace of the money queues — not the audit log beside it. The
       * two record overlapping events for different jobs
       * (`apps/finance/CONTEXT.md`): this one carries the amount, the ceiling in
       * force and the operator's own remark, and covers deposits and
       * withdrawals only.
       *
       * `live`: every staff act on either queue leaves a row, `markPayoutFailed`
       * — the one that used to be silent while refunding the player — included.
       * `cancelDeposit` writes none, correctly and permanently: no staff member
       * acted, so there is nobody for the trace to name.
       */
      id: 'audit.approvalLogs',
      route: '/audit → Approval logs',
      // Gateway: review-events reads = audit:read (upstreams.ts).
      permission: 'audit:read',
      status: 'live',
      fields: [
        { key: 'createdAt', kind: 'date' },
        { key: 'kind', kind: 'status' },
        { key: 'recordId', kind: 'text' },
        { key: 'action', kind: 'text' },
        { key: 'result', kind: 'status' },
        // Unsigned on purpose: a request's amount and a ceiling are both
        // magnitudes, so there is no direction for a reader to misread.
        { key: 'amount', kind: 'money-unsigned' },
        { key: 'limitAmount', kind: 'money-unsigned' },
        { key: 'overLimit', kind: 'status' },
        { key: 'actor', kind: 'text' },
        { key: 'actorRole', kind: 'text' },
        { key: 'escalatedToLevel', kind: 'count' },
        { key: 'remark', kind: 'text' },
      ],
      cautionCount: 5,
      warningCount: 0,
    },
    {
      id: 'audit.securityEvents',
      route: '/audit → Security events',
      permission: 'audit:read',
      status: 'records-intent',
      fields: [
        { key: 'createdAt', kind: 'date' },
        { key: 'eventType', kind: 'status' },
        { key: 'actor', kind: 'text' },
        { key: 'ipAddress', kind: 'text' },
      ],
      cautionCount: 6,
      warningCount: 0,
    },
  ],
  copy: { th, en, zh, vi, km, lo, my },
};
